import Button from '@components/shared/Button'
import RenderTokenIcon from '@components/shared/RenderTokenIcon'
import TabUnderline from '@components/shared/TabUnderline'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import useSettingsStore from '@store/settingsStore'
import clsx from 'clsx'
import { ApproveTokenButton } from 'components/ApproveTokenButton/ApproveTokenButton'
import BuyInputSection from 'components/BuyInputSection/BuyInputSection'
import { getSyntheticsDepositIndexTokenKey } from 'config/localStorage'
import { getV2Tokens } from 'config/tokens'
import { MAX_METAMASK_MOBILE_DECIMALS } from 'config/ui'
import { useHasOutdatedUi } from 'domain/legacy'
import { createCommonPoolDeposit } from 'domain/synthetics/markets/createCommonPoolDeposit'
import { createCommonPoolWithdrawal } from 'domain/synthetics/markets/createCommonPoolWithdrawal'
import { Market, MarketsInfoData } from 'domain/synthetics/markets/types'
import { getMarketIndexName } from 'domain/synthetics/markets/utils'
import {
  TokenData,
  TokensData,
  convertToUsd,
  getNeedTokenApprove,
  getTokenData,
  useTokenBalances,
  useTokensAllowanceData,
} from 'domain/synthetics/tokens'
import { getCommonError } from 'domain/synthetics/trade/utils/validation'
import { Token } from 'domain/tokens'
import { BigNumber } from 'ethers'
import { useChainId } from 'rfx/lib/chains'
import { DUST_BNB } from 'rfx/lib/legacy'
import { useLocalStorageSerializeKey } from 'rfx/lib/localStorage'
import {
  formatAmountFree,
  formatTokenAmount,
  formatUsd,
  limitDecimals,
  parseValue,
} from 'rfx/lib/numbers'
import { getByKey } from 'rfx/lib/objects'
import { usePendingTxns } from 'rfx/lib/usePendingTxns'
import { useSafeState } from 'rfx/lib/useSafeState'
import useIsMetamaskMobile from 'rfx/lib/wallets/useIsMetamaskMobile'
import useWallet from 'rfx/lib/wallets/useWallet'
import useTokenPrice from 'hooks/rfx/usePyth/useTokenPrice'
import { usePaymaster } from 'hooks/usePaymaster'
import { uniq } from 'lodash'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { isMarketDisabled } from 'utils/markets'
import commonPoolStore from '@store/commonPoolStore'

export enum Operation {
  Deposit = 'Deposit',
  Withdrawal = 'Withdrawal',
}

export enum Mode {
  Single = 'Single',
  Pair = 'Pair',
}

type Props = {
  selectedMarketAddress?: string
  markets: Market[]
  marketsInfoData?: MarketsInfoData
  tokensData?: TokensData
  onSelectMarket: (marketAddress: string) => void
  setPendingTxns: (txns: any) => void
  operation: Operation
  shouldDisableValidation?: boolean
  mode: Mode
  setMode: Dispatch<SetStateAction<Mode>>
  setOperation: Dispatch<SetStateAction<Operation>>
}

export function VaultFormBox(p: Props) {
  const {
    operation,
    setOperation,
    onSelectMarket,
    marketsInfoData,
    tokensData,
    shouldDisableValidation,
  } = p
  const isPro = useSettingsStore((state) => state.isPro())

  const isMetamaskMobile = useIsMetamaskMobile()
  const { openConnectModal } = useConnectModal()

  const [, setPendingTxns] = usePendingTxns()
  const marketAddress = p.selectedMarketAddress

  const { chainId } = useChainId()
  const { account, signer } = useWallet()
  const { balancesData } = useTokenBalances(chainId)

  const { data: hasOutdatedUi } = useHasOutdatedUi()

  const { sendPaymasterTransaction } = usePaymaster()

  const operationLabels = {
    [Operation.Deposit]: `Add`,
    [Operation.Withdrawal]: `Remove`,
  }
  const selectedVault = commonPoolStore((store) => store.selectedVault)
  const commonPoolAddress = selectedVault.value
  const commonPoolAsset = selectedVault.label

  const isDeposit = operation === Operation.Deposit

  const marketInfo = getByKey(marketsInfoData, marketAddress)

  const [marketTokenInputValue, setMarketTokenInputValue] =
    useSafeState<string>()

  const marketToken = Object.values(getV2Tokens(chainId)).find(
    (item) => item.address === commonPoolAddress,
  ) as Token

  const marketTokenPrice = useTokenPrice(
    marketToken?.pythId || '',
    marketToken?.address || '',
    'maxPrice',
  )

  const marketTokenAmount = parseValue(
    marketTokenInputValue || '0',
    marketToken?.decimals || 0,
  )!

  const marketTokenUsd = convertToUsd(
    marketTokenAmount,
    marketToken?.decimals,
    marketTokenPrice,
  )!

  const markets = Object.values(marketsInfoData || {}).filter(
    (marketInfo) => !isMarketDisabled(marketInfo),
  )
  const [indexName, setIndexName] = useLocalStorageSerializeKey<
    string | undefined
  >(getSyntheticsDepositIndexTokenKey(chainId), undefined)

  const payTokenAddresses = useMemo(() => {
    const addresses: string[] = []
    if (isDeposit) {
      addresses.push(
        Object.values(tokensData || {}).filter(
          (item) => item.symbol === commonPoolAsset,
        )[0]?.address,
      )
    } else {
      addresses.push(marketToken?.address)
    }

    return addresses
  }, [tokensData, isDeposit, marketToken, commonPoolAsset])

  const { tokensAllowanceData } = useTokensAllowanceData(chainId, {
    spenderAddress: commonPoolAddress,
    tokenAddresses: payTokenAddresses,
  })
  const isAllowanceLoaded = !!tokensAllowanceData

  const firstToken = getTokenData(
    tokensData,
    Object.values(tokensData || {}).filter(
      (item) => item.symbol === commonPoolAsset,
    )[0]?.address,
  ) as TokenData

  const firstTokenPrice = useTokenPrice(
    firstToken?.pythId || '',
    firstToken?.address || '',
    'maxPrice',
  )
  const [firstTokenInputValue, setFirstTokenInputValue] =
    useSafeState<string>('')
  const firstTokenAmount = parseValue(
    firstTokenInputValue,
    firstToken?.decimals || 0,
  )
  const firstTokenUsd = convertToUsd(
    firstTokenAmount,
    firstToken?.decimals,
    firstTokenPrice,
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  const tokensToApprove = (function getTokensToApprove() {
    const addresses: string[] = []

    if (!tokensAllowanceData) {
      return addresses
    }

    if (isDeposit) {
      if (
        firstTokenAmount?.gt(0) &&
        getNeedTokenApprove(
          tokensAllowanceData,
          firstToken?.address,
          firstTokenAmount,
        )
      ) {
        addresses.push(firstToken.address)
      }
    } else {
      if (
        marketTokenAmount?.gt(0) &&
        getNeedTokenApprove(
          tokensAllowanceData,
          marketToken?.address,
          marketTokenAmount,
        )
      ) {
        addresses.push(marketToken?.address)
      }
    }

    return uniq(addresses)
  })()

  const submitState = useMemo(() => {
    const commonError = getCommonError({
      chainId,
      isConnected: true,
      hasOutdatedUi,
    })[0]

    const error = commonError

    if (!account) {
      return {
        text: `Connect Wallet`,
        isDisabled: true,
        onSubmit: () => {},
      }
    }

    const onSubmit = () => {}

    if (error) {
      return {
        text: error,
        error,
        isDisabled: !shouldDisableValidation,
        onSubmit,
      }
    }

    return {
      text: isDeposit
        ? `Buy SLV ${commonPoolAsset}`
        : `Sell SLV ${commonPoolAsset}`,
      onSubmit,
    }
  }, [
    account,
    chainId,
    hasOutdatedUi,
    isDeposit,
    marketInfo,
    marketToken,
    marketTokenAmount,
    openConnectModal,
    shouldDisableValidation,
    commonPoolAsset,
  ])

  const resetInputs = useCallback(() => {
    setFirstTokenInputValue('')
    setMarketTokenInputValue('')
  }, [setFirstTokenInputValue, setMarketTokenInputValue])

  const onOperationChange = useCallback(
    (operation: Operation) => {
      resetInputs()
      setOperation(operation)
    },
    [resetInputs, setOperation],
  )

  const onMarketChange = useCallback(
    (marketAddress: string) => {
      resetInputs()
      onSelectMarket(marketAddress)
    },
    [onSelectMarket, resetInputs],
  )

  useEffect(
    function updateIndexToken() {
      if (!indexName && markets.length) {
        setIndexName(getMarketIndexName(markets[0]))
      }
    },
    [indexName, markets, setIndexName],
  )

  useEffect(
    function updateMarket() {
      const marketsByIndexName = markets.filter(
        (market) => getMarketIndexName(market) === indexName,
      )

      if (!marketsByIndexName.length) {
        return
      }

      if (
        !marketAddress ||
        !marketsByIndexName.find(
          (market) => market.marketTokenAddress === marketAddress,
        )
      ) {
        onMarketChange(marketsByIndexName[0].marketTokenAddress)
      }
    },
    [indexName, marketAddress, markets, onMarketChange],
  )

  function onCreateDeposit() {
    if (!account || !firstTokenAmount || !signer) {
      return Promise.resolve()
    }

    return createCommonPoolDeposit(chainId, signer, {
      account,
      amount: firstTokenAmount,
      setPendingTxns,
      sendPaymasterTransaction,
      commonPoolAddress,
    })
  }

  function onCreateWithdrawal() {
    if (!account || !marketTokenAmount || !signer) {
      return Promise.resolve()
    }

    return createCommonPoolWithdrawal(chainId, signer, {
      account,
      amount: marketTokenAmount,
      setPendingTxns,
      sendPaymasterTransaction,
      commonPoolAddress,
    })
  }

  const submitButtonState = (function getSubmitButtonState() {
    if (!account) {
      return {
        text: `Connect Wallet`,
        isDisabled: true,
        onSubmit: () => {},
      }
    }
    if (firstToken?.balance?.lt(firstTokenAmount || 0)) {
      return {
        text: `Insufficient Balance`,
        isDisabled: true,
        onSubmit: () => {},
      }
    }
    if (payTokenAddresses.length > 0 && !isAllowanceLoaded) {
      return {
        text: `Loading...`,
        disabled: true,
      }
    }

    const onSubmit = () => {
      setIsSubmitting(true)

      let txnPromise: Promise<any>

      if (isDeposit) {
        txnPromise = onCreateDeposit()
      } else {
        txnPromise = onCreateWithdrawal()
      }

      txnPromise
        .then(() => {})
        .finally(() => {
          setIsSubmitting(false)
        })
    }

    if (isSubmitting) {
      return {
        text: isDeposit
          ? `Buying SLV ${commonPoolAsset}...`
          : `Selling SLV ${commonPoolAsset}...`,
        disabled: true,
      }
    }

    if (tokensToApprove?.length > 0 && marketToken) {
      const symbols = tokensToApprove?.map((address) => {
        const token = getTokenData(tokensData, address)!
        return address === marketToken.address
          ? 'SLV ' + commonPoolAsset
          : token?.assetSymbol ?? token?.symbol
      })

      const symbolsText = symbols.join(', ')

      return {
        text: `Pending ${symbolsText} approval`,
        disabled: true,
      }
    }

    const operationText = isDeposit ? `Buy` : `Sell`
    const text = `Confirm ${operationText}`

    return {
      text,
      onClick: onSubmit,
    }
  })()

  const btnColor =
    payTokenAddresses.length > 0 && !isAllowanceLoaded
      ? ''
      : isDeposit
        ? 'bg-th-success'
        : 'bg-th-error'

  return (
    <div className="w-full">
      <div className="pt-2 lg:pt-4">
        <TabUnderline
          activeValue={operation}
          values={[Operation.Deposit, Operation.Withdrawal]}
          names={[
            operationLabels[Operation.Deposit],
            operationLabels[Operation.Withdrawal],
          ]}
          onChange={onOperationChange}
          small
        />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submitState?.onSubmit()
        }}
      >
        <div
          className={`flex flex-col`}
          style={{ flexDirection: isDeposit ? 'column-reverse' : 'column' }}
        >
          {isDeposit ? (
            <BuyInputSection
              topLeftLabel={`Pay`}
              topLeftValue={formatUsd(firstTokenUsd)}
              topRightLabel={`Balance`}
              topRightValue={formatTokenAmount(
                firstToken?.balance,
                firstToken?.decimals,
                '',
                {
                  useCommas: true,
                },
              )}
              {...(isDeposit && {
                onClickTopRightLabel: () => {
                  if (firstToken?.balance) {
                    const maxAvailableAmount = firstToken.isNative
                      ? firstToken.balance.sub(BigNumber.from(DUST_BNB).mul(2))
                      : firstToken.balance
                    setFirstTokenInputValue(
                      formatAmountFree(maxAvailableAmount, firstToken.decimals),
                    )
                  }
                },
              })}
              showMaxButton={
                isDeposit &&
                firstToken?.balance?.gt(0) &&
                !firstTokenAmount?.eq(firstToken.balance)
              }
              inputValue={firstTokenInputValue}
              onInputValueChange={(e) => {
                if (firstToken) {
                  setFirstTokenInputValue(e.target.value)
                }
              }}
              onClickMax={() => {
                if (firstToken?.balance) {
                  const maxAvailableAmount = firstToken.isNative
                    ? firstToken.balance.sub(BigNumber.from(DUST_BNB).mul(2))
                    : firstToken.balance

                  const formattedMaxAvailableAmount = formatAmountFree(
                    maxAvailableAmount,
                    firstToken.decimals,
                  )
                  const finalAmount = isMetamaskMobile
                    ? limitDecimals(
                        formattedMaxAvailableAmount,
                        MAX_METAMASK_MOBILE_DECIMALS,
                      )
                    : formattedMaxAvailableAmount

                  setFirstTokenInputValue(finalAmount)
                }
              }}
            >
              <div className="flex items-center gap-2">
                <RenderTokenIcon
                  size="small"
                  symbol={isDeposit ? firstToken?.symbol : marketToken?.symbol}
                  state={firstTokenInputValue ? 'active' : 'default'}
                />
                <div className="text-sm font-medium text-th-fgd-1">
                  {isDeposit ? firstToken?.symbol : marketToken?.symbol}
                </div>
              </div>
            </BuyInputSection>
          ) : (
            <BuyInputSection
              topLeftLabel={`Pay`}
              topLeftValue={
                marketTokenUsd?.gt(0) ? formatUsd(marketTokenUsd) : ''
              }
              topRightLabel={`Balance`}
              topRightValue={
                balancesData &&
                formatTokenAmount(
                  balancesData?.[marketToken?.address] || 0,
                  marketToken?.decimals,
                  '',
                  {
                    useCommas: true,
                  },
                )
              }
              showMaxButton={
                balancesData && balancesData[marketToken?.address]?.gt(0)
              }
              inputValue={marketTokenInputValue}
              onInputValueChange={(e) => {
                setMarketTokenInputValue(e.target.value)
              }}
              {...{
                onClickTopRightLabel: () => {
                  if (
                    balancesData &&
                    balancesData[marketToken?.address as string]
                  ) {
                    setMarketTokenInputValue(
                      formatAmountFree(
                        balancesData[marketToken?.address],
                        marketToken.decimals,
                      ),
                    )
                  }
                },
              }}
              onClickMax={() => {
                if (
                  marketToken?.address &&
                  balancesData &&
                  (balancesData[marketToken?.address] as BigNumber)
                ) {
                  const formattedGMBalance = formatAmountFree(
                    balancesData[marketToken?.address],
                    marketToken.decimals,
                  )
                  const finalGMBalance = isMetamaskMobile
                    ? limitDecimals(
                        formattedGMBalance,
                        MAX_METAMASK_MOBILE_DECIMALS,
                      )
                    : formattedGMBalance
                  setMarketTokenInputValue(finalGMBalance)
                }
              }}
            />
          )}
        </div>

        {tokensToApprove && tokensToApprove.length > 0 ? (
          <div className="py-4">
            {tokensToApprove.map((address) => {
              const token = getTokenData(tokensData, address)!
              return (
                <div key={address}>
                  <ApproveTokenButton
                    key={address}
                    tokenAddress={address}
                    tokenSymbol={
                      address === marketToken?.address
                        ? 'SLV ' + commonPoolAsset
                        : token.assetSymbol ?? token.symbol
                    }
                    spenderAddress={commonPoolAddress}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <div className="Exchange-swap-button-container !py-4">
            <Button
              className={clsx('w-full border-none text-sm uppercase', btnColor)}
              type="submit"
              secondary={btnColor !== ''}
              onClick={submitButtonState.onClick}
              disabled={submitButtonState.disabled}
              size={isPro ? 'medium' : 'large'}
            >
              {submitButtonState.text}
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}
